import {
  BitkitButton,
  BitkitCodeSnippet,
  BitkitDialog,
  BitkitExpandableCard,
  BitkitLink,
  BitkitList,
  IconArrowNortheast,
  IconQuestionCircle,
} from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Text } from '@chakra-ui/react/text';
import { MouseEvent, useEffect, useRef, useState } from 'react';

import URLs from '@/enums/urls';
import { McpConnectionStatus } from '@/models/User';

import MCP_CLIENTS, { McpClient, McpClientSnippet, McpClientStep } from './mcpClients';
import {
  McpSignpostSource,
  trackMcpClientSetupAttempted,
  trackMcpSetupDialogDismissed,
  trackMcpSetupDialogFinished,
  trackMcpSetupHelpOpened,
} from './mcpTracking';
import ProviderCard from './ProviderCard';

type SnippetProps = McpClientSnippet & { onCopy: () => void };

const isCopyButtonClick = (event: MouseEvent<HTMLElement>) => Boolean((event.target as HTMLElement).closest('button'));

const Snippet = ({ code, variant, onCopy }: SnippetProps) => (
  <Box
    display="contents"
    onClickCapture={(event) => {
      if (isCopyButtonClick(event)) onCopy();
    }}
  >
    {variant === 'multi' ? (
      <BitkitCodeSnippet variant="multi" wrapLines={false}>
        {code}
      </BitkitCodeSnippet>
    ) : (
      <BitkitCodeSnippet variant="single">{code}</BitkitCodeSnippet>
    )}
  </Box>
);

const StepText = ({ step }: { step: McpClientStep }) => (
  <>
    {step.text}
    {step.link && (
      <BitkitLink colorVariant="purple" href={step.link.href}>
        {step.link.label}
      </BitkitLink>
    )}
  </>
);

const StepWithSnippet = ({ step, code, variant, onCopy }: { step: McpClientStep } & SnippetProps) => (
  <Box display="flex" flexDirection="column" gap="8" minWidth="0" overflow="hidden">
    <Text textStyle="body/md/regular" color="text/body">
      <StepText step={step} />
    </Text>
    <Snippet code={code} variant={variant} onCopy={onCopy} />
  </Box>
);

type ClientInstructionsProps = {
  client: McpClient;
  onCopy: () => void;
  onInstallLinkOpen: () => void;
  onTokenFallbackOpen: () => void;
};

const ClientInstructions = ({ client, onCopy, onInstallLinkOpen, onTokenFallbackOpen }: ClientInstructionsProps) => {
  const [isTokenFallbackExpanded, setIsTokenFallbackExpanded] = useState(false);

  return (
    <Box display="flex" flexDirection="column" gap="16">
      {client.deeplink && (
        <Box display="flex" flexDirection="column" gap="8">
          <BitkitButton href={client.deeplink.href} width="100%" onClick={onInstallLinkOpen}>
            {client.deeplink.label}
          </BitkitButton>
          <Text textStyle="body/md/regular" color="text/secondary" textAlign="center">
            {client.deeplink.note}
          </Text>
        </Box>
      )}

      <Box display="flex" flexDirection="column" gap="8">
        {client.label && (
          <Text textStyle="body/md/semibold" color="text/primary">
            {client.label}
          </Text>
        )}

        {client.steps && (
          <BitkitList variant="explainer" size="md" textColor="body">
            {client.steps.map((step) => (
              <BitkitList.Item key={step.text}>
                {step.snippet ? (
                  <StepWithSnippet
                    step={step}
                    code={step.snippet.code}
                    variant={step.snippet.variant}
                    onCopy={onCopy}
                  />
                ) : (
                  <StepText step={step} />
                )}
              </BitkitList.Item>
            ))}
          </BitkitList>
        )}

        {client.snippet && <Snippet code={client.snippet.code} variant={client.snippet.variant} onCopy={onCopy} />}

        {client.note && (
          <Text textStyle="body/md/regular" color="text/secondary">
            {client.note}
          </Text>
        )}
      </Box>

      {client.fallback && (
        <BitkitExpandableCard
          size="md"
          title={client.fallback.title}
          icon={IconQuestionCircle}
          expanded={isTokenFallbackExpanded}
          onChange={(expanded) => {
            setIsTokenFallbackExpanded(expanded);
            if (expanded) onTokenFallbackOpen();
          }}
        >
          <Box display="flex" flexDirection="column" gap="8" minWidth="0" overflow="hidden">
            <Text textStyle="body/md/regular" color="text/body">
              {client.fallback.text}
            </Text>
            {client.fallback.snippet && (
              <Snippet code={client.fallback.snippet.code} variant={client.fallback.snippet.variant} onCopy={onCopy} />
            )}
          </Box>
        </BitkitExpandableCard>
      )}
    </Box>
  );
};

type ConnectMcpDialogProps = {
  open: boolean;
  onClose: () => void;
  source: McpSignpostSource;
  status?: McpConnectionStatus;
};

const ConnectMcpDialog = ({ open, onClose, source, status = 'none' }: ConnectMcpDialogProps) => {
  const [selectedId, setSelectedId] = useState(MCP_CLIENTS[0].id);
  const selectedClient = MCP_CLIENTS.find((client) => client.id === selectedId) ?? MCP_CLIENTS[0];
  const signpost = { source, mcpConnectionStatus: status };
  const isEscapePressedRef = useRef(false);

  useEffect(() => {
    if (!open) return undefined;

    isEscapePressedRef.current = false;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') isEscapePressedRef.current = true;
    };
    document.addEventListener('keydown', handleKeyDown, true);

    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [open]);

  const handleDismiss = () => {
    trackMcpSetupDialogDismissed({
      ...signpost,
      mcpClient: selectedClient.id,
      dismissMethod: isEscapePressedRef.current ? 'other' : 'close_button',
    });
    onClose();
  };

  const handleDone = () => {
    trackMcpSetupDialogFinished({ ...signpost, mcpClient: selectedClient.id });
    onClose();
  };

  return (
    <BitkitDialog
      title="Connect Bitrise MCP"
      size="md"
      open={open}
      onOpenChange={({ open: next }) => {
        if (!next) handleDismiss();
      }}
      footerButtons={
        <>
          {/* The footer row is right-aligned, so the auto margin is what parks the guide on the left. */}
          <BitkitButton
            variant="tertiary"
            href={URLs.BITRISE_MCP}
            isExternal
            suffixIcon={IconArrowNortheast}
            marginInlineEnd="auto"
            onClick={() =>
              trackMcpSetupHelpOpened({ ...signpost, mcpClient: selectedClient.id, helpType: 'full_install_guide' })
            }
          >
            Full install guide
          </BitkitButton>
          <BitkitButton variant="secondary" onClick={handleDone}>
            Done
          </BitkitButton>
        </>
      }
    >
      <Box display="flex" flexDirection="column" gap="24">
        <Text textStyle="body/lg/regular" color="text/body">
          Talk to Bitrise from your AI client.
        </Text>

        {status === 'expired' && (
          <Text textStyle="body/md/regular" color="status/warning/text">
            Your MCP connection has expired. Sign in to Bitrise again from your client to reconnect.
          </Text>
        )}

        <Box display="flex" flexDirection="column" gap="16">
          <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap="8">
            {MCP_CLIENTS.map((client) => (
              <ProviderCard
                key={client.id}
                name={client.name}
                icon={client.icon}
                isSelected={client.id === selectedId}
                onClick={() => setSelectedId(client.id)}
              />
            ))}
          </Box>

          <ClientInstructions
            client={selectedClient}
            onCopy={() =>
              trackMcpClientSetupAttempted({
                ...signpost,
                mcpClient: selectedClient.id,
                setupMethod: 'config_snippet_copied',
              })
            }
            onInstallLinkOpen={() =>
              trackMcpClientSetupAttempted({
                ...signpost,
                mcpClient: selectedClient.id,
                setupMethod: 'install_link_opened',
              })
            }
            onTokenFallbackOpen={() =>
              trackMcpSetupHelpOpened({ ...signpost, mcpClient: selectedClient.id, helpType: 'token_fallback' })
            }
          />
        </Box>
      </Box>
    </BitkitDialog>
  );
};

export default ConnectMcpDialog;
